//
// # `DrawTo` Implementation for HTML Canvas
//

// Workspace Imports
import { model, graphics } from "GenAlphaModel";
import { schema } from "GenAlphaSchema";
// Local Imports
import * as style from "DrawCore/src/style";
import {
  type DrawTo,
  type HasXform,
  type InstanceDrawResult,
  type LayoutDisplayOptions,
  type RunFn,
  type SchematicDisplayOptions,
  MAX_PIXEL_AREA,
  MIN_CULL_SCREEN_PX,
} from "DrawCore/src/draw/drawto";
import { DrawToBase } from "DrawCore/src/draw/base";
import { DrawnTextOrientation } from "DrawCore/src/draw/text_orientation";
import type { HtmlCanvas } from "./root";
import { Sprite, Key } from "./sprite";
import { ViewPort } from "./viewport";
import type {
  ElementBucket,
  OverlayKind,
  RenderStats,
} from "DrawCore/src/stats";

export class DrawToHtmlCanvas extends DrawToBase {
  //
  // World transform information, exposed via the DrawTo interface so that
  // entity redraw methods can access scale and viewport without walking
  // the entity tree back to the editor.
  override readonly scale: number;
  override readonly viewport: model.Bbox | null;

  // Rendering context, derived from the HtmlCanvas root so that entity
  // redraw methods can access PDK and layer visibility without walking
  // the entity parent chain. This enables shared entity trees.
  override readonly pdk: schema.pdk.PDK | null;
  override readonly hiddenLayers: ReadonlySet<number>;

  // Layout display options, derived from the HtmlCanvas root.
  override readonly layoutDisplay: LayoutDisplayOptions | null;

  // Schematic label visibility, derived from the HtmlCanvas root.
  override readonly schematicDisplay: SchematicDisplayOptions | null;

  // Per-frame render stats, shared with the root HtmlCanvas.
  override readonly stats: RenderStats;

  // The overlay kind currently in effect, or null when primitive draws are
  // not tagged as overlays (e.g. instance geometry inside sprites).
  // Set by `overlay(kind, fn)` for the duration of `fn`.
  private currentOverlay: OverlayKind | null = null;

  // The "base" bucket that primitive draws count against when no overlay
  // scope is active. Set per-DrawTo-instance based on how the DrawTo was
  // constructed: the root canvas DrawTo defaults to "direct"; the DrawTo
  // a Sprite builds its content into is "sprite"; the DrawTo the vector
  // branch creates in `instance()` is "vector". Overlay scope takes
  // precedence over this when set.
  readonly baseBucket: Exclude<ElementBucket, "overlay">;

  constructor(
    readonly root: HtmlCanvas, // The root `HtmlCanvas` instance
    readonly ctx: CanvasRenderingContext2D, // The canvas rendering context
    readonly cullViewport: ViewPort, // Viewport. Instances outside this will not be rendered.
    baseBucket: Exclude<ElementBucket, "overlay"> = "direct"
  ) {
    super();
    this.scale = root.worldTransforms.schToPage.scale;
    // Derive viewport from cullViewport: when culling is infinite (e.g. inside
    // sprites), set viewport to null so callers know viewport-based decisions
    // (like early-out culling in ScalarInstanceEntity.redraw) are not valid.
    this.viewport = cullViewport.kind === "finite" ? cullViewport.bbox : null;
    // Propagate rendering context from the root HtmlCanvas.
    // These are set by LayoutContent before each render call.
    this.pdk = root.pdk;
    this.hiddenLayers = root.hiddenLayers;
    // Propagate layout display options from the root.
    this.layoutDisplay = root.layoutDisplay;
    // Propagate schematic label visibility from the root.
    this.schematicDisplay = root.schematicDisplay;
    // Share stats with the root so sprite / vector sub-`DrawTo`s bump the
    // same counters.
    this.stats = root.stats;
    this.baseBucket = baseBucket;
  }

  // Run `fn` with the given overlay kind as the current scope.
  // Nested calls temporarily override, then restore on exit.
  override overlay(kind: OverlayKind, fn: RunFn): unknown {
    const prev = this.currentOverlay;
    this.currentOverlay = kind;
    try {
      return fn(this);
    } finally {
      this.currentOverlay = prev;
    }
  }

  // Bump the overlay `drawn` count for the current overlay kind (if any).
  // Called from every primitive draw method.
  private bumpOverlayDrawn(): void {
    if (this.currentOverlay !== null) {
      this.stats.overlays[this.currentOverlay].drawn += 1;
    }
  }

  // Bump the total `elementsDrawn` counter and the appropriate per-bucket
  // counter. Called once per primitive draw. Overlay scope takes precedence
  // over the DrawTo's `baseBucket`; otherwise primitives are attributed to
  // sprite / vector / direct based on how this DrawTo was constructed.
  private bumpElement(): void {
    const s = this.stats;
    s.elementsDrawn += 1;
    if (this.currentOverlay !== null) {
      s.elements.overlay += 1;
    } else {
      s.elements[this.baseBucket] += 1;
    }
  }

  // Bump the overlay `culled` count for the current overlay kind (if any).
  override overlayCulled(): void {
    if (this.currentOverlay !== null) {
      this.stats.overlays[this.currentOverlay].culled += 1;
    }
  }

  // Bump the overlay `sizeCulled` count for the current overlay kind (if any).
  override overlaySizeCulled(): void {
    if (this.currentOverlay !== null) {
      this.stats.overlays[this.currentOverlay].sizeCulled += 1;
    }
  }

  // Bump the element-level viewport-cull counter. Called by `redrawElements`
  // et al. when an element's bbox is outside the current viewport.
  override elementCulled(): void {
    this.stats.elements.culled += 1;
  }

  // Bump the element-level size-cull counter. Called by `redrawElements`
  // et al. when an element would render below the pixel threshold.
  override elementSizeCulled(): void {
    this.stats.elements.sizeCulled += 1;
  }

  // Draw within a child group, and clean up after
  group(xform: model.Xform, fn: (to: DrawTo) => unknown): unknown {
    this.apply(xform);
    const r = fn(this);
    this.restore();
    return r;
  }

  // Apply transformation to the canvas context
  // GDS transform order: reflect (about X-axis) -> rotate -> translate
  // Canvas transforms apply in reverse order to content, so we call:
  // translate -> reflect -> rotate (which gives: rotate -> reflect -> translate on content)
  // But we need GDS order, so we call: translate -> rotate -> reflect
  // Actually wait - canvas applies most recent transform first to drawn content.
  // So for content to see: reflect -> rotate -> translate, we need to call:
  // translate -> rotate -> reflect (reverse order)
  private apply(xform: model.Xform): void {
    const { x, y, reflected, rotation } = xform;

    this.ctx.save(); // Save current state
    this.ctx.translate(x, y); // Apply translation

    // Apply rotation
    // Note: World transform has Y-flip (scale -scale), which reverses visual rotation direction.
    // GDS uses counterclockwise rotation, so we use radiansCounterClockwise.
    if (rotation !== 0) {
      const radians = model.Rotation.radiansCounterClockwise(rotation);
      this.ctx.rotate(radians);
    }

    // Apply reflection (Y-axis flip / reflection about X-axis)
    // This must come AFTER rotate in our call order so it applies BEFORE rotate to content
    if (reflected) {
      this.ctx.scale(1, -1);
    }
  }

  // Restore transform state
  private restore(): void {
    this.ctx.restore();
  }

  // Draw within a child layer, and clean up after
  layer(fn: (to: DrawTo) => unknown): unknown {
    return fn(this);
  }

  // Core drawing primitive implementations
  bbox(data: graphics.Bbox, s: style.ShapeStyle): unknown {
    if (s.hidden) return null;
    const { min } = data;
    const { width, height } = model.Bbox.size(data);

    this.applyShapeStyle(s);
    this.ctx.beginPath();
    this.ctx.rect(min.x, min.y, width, height);
    this.applyStrokeFill(s);

    this.bumpElement();
    this.bumpOverlayDrawn();
    return null;
  }

  path(data: graphics.Path, s: style.ShapeStyle): unknown {
    if (s.hidden) return null;
    if (data.points.length < 2) return null;

    this.applyShapeStyle(s);
    // A path is a stroked wire trace, not a fillable shape: its own real
    // width (from `data.style.width`, not the generic per-layer
    // `s.linewidth`) drives the line width, never-thinner-than-1px so it
    // stays visible zoomed out, and `beginExt`/`endExt` are baked into the
    // drawn points (see `graphics.Path.drawPoints`) rather than expressed
    // via a native canvas cap, since a cap can only do flush (0) or
    // half-linewidth extension, not an arbitrary amount.
    const minWorld = this.scale > 0 ? 1 / this.scale : 0;
    this.ctx.lineWidth = Math.max(data.style.width, minWorld);
    this.ctx.lineCap = "butt";

    this.ctx.beginPath();
    const [first, ...rest] = graphics.Path.drawPoints(data);
    this.ctx.moveTo(first.x, first.y);
    for (const point of rest) {
      this.ctx.lineTo(point.x, point.y);
    }
    // Stroke only — never `ctx.fill()` here. This subpath is deliberately
    // left open (no `closePath()`); Canvas2D's `fill()` auto-closes the
    // last open subpath to fill it, which would silently draw the polygon
    // formed by connecting the raw centerline points instead of a thin
    // stroked line (the bug this replaced).
    if (s.stroke && s.stroke !== style.Special.None) {
      this.ctx.strokeStyle = style.Color.str(s.stroke);
      this.ctx.stroke();
    }

    this.bumpElement();
    this.bumpOverlayDrawn();
    return null;
  }

  polygon(data: graphics.Polygon, s: style.ShapeStyle): unknown {
    if (s.hidden) return null;
    if (data.points.length < 3) return null;

    this.applyShapeStyle(s);
    this.ctx.beginPath();
    const [first, ...rest] = data.points;
    this.ctx.moveTo(first.x, first.y);
    for (const point of rest) {
      this.ctx.lineTo(point.x, point.y);
    }
    this.ctx.closePath();
    this.applyStrokeFill(s);

    this.bumpElement();
    this.bumpOverlayDrawn();
    return null;
  }

  arc(data: graphics.Arc, s: style.ShapeStyle): unknown {
    if (s.hidden) return null;
    const { ellipseBbox, startAngle, stopAngle } = data;
    const { x, y } = model.Bbox.center(ellipseBbox);
    const { width, height } = model.Bbox.size(ellipseBbox);
    const radiusX = width / 2;
    const radiusY = height / 2;

    this.applyShapeStyle(s);

    this.ctx.beginPath();

    // Handle arc wrapping around 0
    if (startAngle > stopAngle) {
      // Draw two arcs
      this.ctx.ellipse(
        x,
        y,
        radiusX,
        radiusY,
        0,
        startAngle,
        2 * Math.PI,
        false
      );
      this.ctx.ellipse(x, y, radiusX, radiusY, 0, 0, stopAngle, false);
    } else {
      this.ctx.ellipse(x, y, radiusX, radiusY, 0, startAngle, stopAngle, false);
    }

    this.applyStrokeFill(s);

    this.bumpElement();
    this.bumpOverlayDrawn();
    return null;
  }

  ellipse(data: graphics.Ellipse, s: style.ShapeStyle): unknown {
    if (s.hidden) return null;
    const { bbox } = data;
    const { x, y } = model.Bbox.center(bbox);
    const { width, height } = model.Bbox.size(bbox);
    const radiusX = width / 2;
    const radiusY = height / 2;

    this.applyShapeStyle(s);
    this.ctx.beginPath();
    this.ctx.ellipse(x, y, radiusX, radiusY, 0, 0, 2 * Math.PI, false);
    this.applyStrokeFill(s);

    this.bumpElement();
    this.bumpOverlayDrawn();
    return null;
  }

  text(data: graphics.Text, s: style.TextStyle, parent: HasXform): unknown {
    // Return immediately if the text is not to be visible
    if (data.hidden || s.hidden) return null;

    this.ctx.save();
    this.applyTextStyle(s);

    const drawnTextOrientation = DrawnTextOrientation.fromParent(data, parent);
    const { alignment, baseline, rotation, reflect } = drawnTextOrientation;
    const { x, y } = data.loc; // Position the text

    // Apply everything from the orientation
    this.ctx.textAlign = alignment;
    this.ctx.textBaseline = baseline;
    this.ctx.translate(x, y);
    this.ctx.rotate(model.Rotation.radiansClockwise(rotation));
    // NOTE: this is another y-axis inversion step right here:
    this.ctx.scale(reflect.horiz ? -1 : 1, reflect.vert ? 1 : -1);
    this.ctx.fillText(data.text, 0, 0);

    this.ctx.restore();
    this.bumpElement();
    this.bumpOverlayDrawn();
    return null;
  }

  private applyShapeStyle(s: style.ShapeStyle): void {
    this.ctx.lineWidth = s.linewidth;
    this.ctx.lineCap = s.cap;
    this.ctx.lineJoin = s.join;
    this.ctx.setLineDash(s.lineDash ?? []);
  }

  private applyStrokeFill(s: style.ShapeStyle): void {
    if (s.fill && s.fill !== style.Special.None) {
      this.ctx.fillStyle = style.Color.str(s.fill);
      this.ctx.fill();
    }

    if (s.stroke && s.stroke !== style.Special.None) {
      this.ctx.strokeStyle = style.Color.str(s.stroke);
      this.ctx.stroke();
    }
  }

  private applyTextStyle(s: style.TextStyle): void {
    if (s.fill && s.fill !== style.Special.None) {
      this.ctx.fillStyle = style.Color.str(s.fill);
    }

    if (s.stroke && s.stroke !== style.Special.None) {
      this.ctx.strokeStyle = style.Color.str(s.stroke);
    }

    this.ctx.font = this.buildFontString(s);
  }

  private buildFontString(s: style.TextStyle): string {
    const parts: string[] = [];
    parts.push(s.style);
    parts.push(s.weight.toString());
    parts.push(`${s.size}px`);
    parts.push(s.family);
    return parts.join(" ");
  }

  //
  // Draw an instance
  //
  // This is really the complicated part, and where most of the performance gains and trade-offs are made.
  // There are three big picture results which can come of each call to `instance()`:
  // 1. The instance is off screen. We cull it, i.e. don't bother drawing anything.
  // 2. Most of the rest of the time, we rasterize the instance to a sprite,
  //    draw that, and (crucially) cache that sprite for use in future frames.
  //    This is why each call to `instance()` requires a `key`, for going back to that cache.
  // 3. If the instance is too big, e.g. it is zoomed in so that it would be 10x the screen size,
  //    we instead draw it directly as a vector. Otherwise sprite drawing can (and does) fail
  //    due to the large size of its target canvas.
  //
  // (In the code below, these are ordered (1, 3, 2).)
  //
  // This decision process generally proceeds recursively, as both sprites and vector instances
  // create their own further "child" instances of `DrawToHtmlCanvas` to draw into.
  // For vector instances we do some math to allow the culling process to continue recursively.
  // For sprites we don't. Otherwise we'd need to figure out different viewports for each instance of each sprite.
  //
  // Instances inside of sprite-drawing *should* always land in path #2,
  // i.e. they should only ever draw more sprites. Maybe that fact can beget some optimization some day.
  // For now they route back to right here and go through the same decision process.
  //
  override instance(
    key: Key, // Cache key
    bbox: model.Bbox, // Bounding box, in the instantiated's coordinates
    xform: model.Xform, // Transform to apply to the instance
    fn: (to: DrawTo) => unknown
  ): InstanceDrawResult {
    //
    // IMPORTANT: this method returns its OWN draw outcome. The return values
    // of any nested `fn` / `to.instance()` calls are deliberately discarded
    // so a descendant's cull cannot masquerade as this call's cull. Callers
    // (e.g. `ScalarInstanceEntity.redraw`) rely on this when deciding
    // whether to emit overlays for the just-drawn instance.
    //
    // Culling check - if the bbox is not visible, skip rendering
    let intersection: model.Bbox | null = null;
    if (this.cullViewport.kind === "finite") {
      const xformedBbox = model.Xform.transformBbox(xform, bbox);
      intersection = model.Bbox.intersection(
        xformedBbox,
        this.cullViewport.bbox
      );
      if (!intersection) {
        // Off screen, no need to render
        this.stats.instances.culled += 1;
        return "viewport-culled";
      }
    }

    //
    // Size culling - skip instances that are too small to see on screen.
    // This mirrors the MIN_CULL_SCREEN_PX check applied to individual elements
    // but at the instance level, avoiding sprite cache lookups and drawImage
    // calls for instances that would be smaller than a few pixels.
    const screenScale = this.root.worldTransforms.schToPage.scale;
    if (screenScale > 0) {
      const bboxSz = model.Bbox.size(bbox);
      const screenWidth = bboxSz.width * screenScale;
      const screenHeight = bboxSz.height * screenScale;
      if (
        screenWidth < MIN_CULL_SCREEN_PX &&
        screenHeight < MIN_CULL_SCREEN_PX
      ) {
        this.stats.instances.sizeCulled += 1;
        return "size-culled";
      }
    }

    //
    // Target Scale
    // Render the sprite at 1:1 with the physical pixel size it appears on screen.
    // We multiply by devicePixelRatio so sprites render at full resolution on high-DPI displays.
    // The needsRerender 2x threshold means sprites remain valid across a 2x zoom range
    // before needing re-rendering (displayed between 0.5x and 1x of their rendered resolution).
    const targetScale =
      this.root.worldTransforms.schToPage.scale * this.root.devicePixelRatio;

    //
    // Sizing check - if the bbox is too big, no sprite'ing allowed, only draw directly
    const bboxSize = model.Bbox.size(bbox);
    const targetSize = {
      width: bboxSize.width * targetScale,
      height: bboxSize.height * targetScale,
    };
    // Max sprite size in physical pixels. Scale by DPR so the threshold is consistent
    // in logical pixels across displays (16,000 logical pixels regardless of DPR).
    const MAX_SIZE = 16_000 * this.root.devicePixelRatio;
    // Max total pixel area for a sprite canvas. Browsers silently zero canvases that
    // exceed their GPU memory budget (~268M pixels in Chrome). Use a conservative
    // limit well below that to avoid invisible sprites.
    const targetArea = targetSize.width * targetSize.height;
    if (
      targetSize.width > MAX_SIZE ||
      targetSize.height > MAX_SIZE ||
      targetArea > MAX_PIXEL_AREA
    ) {
      // Too big for a sprite, just draw directly
      this.stats.instances.vector += 1;

      // To create a nested viewport, apply the inverse of `xform` to that intersection.
      // Having no intersection assigned means we're using the full "infinite" viewport,
      const nestedViewport: ViewPort = intersection
        ? ViewPort.finite(model.Xform.inverse(xform).applyToBbox(intersection))
        : ViewPort.infinite();

      // Create a new `DrawTo`, so that it gets the new viewport.
      // The "vector" bucket tag flows through this DrawTo so every primitive
      // drawn inside the vector branch is attributed to vector rendering.
      const vectorDrawTo = new DrawToHtmlCanvas(
        this.root,
        this.ctx,
        nestedViewport,
        "vector"
      );
      // Discard `group`'s return (which is `fn`'s return) so nested
      // `to.instance()` calls inside `fn` cannot influence our caller.
      vectorDrawTo.group(xform, fn);
      return "drawn";
    }

    //
    // Sprite Case
    // Draw a rasterized sprite for the target
    //
    // Track cache hit vs miss separately so a frame's sprite-build cost is
    // visible in the stats. A cache hit is a cheap `drawImage`; a miss
    // triggers full rasterization in the `Sprite` constructor.
    let sprite = this.root.spriteCache.get(key);
    let built = false;
    if (!sprite) {
      sprite = new Sprite(this.root, bbox, fn, targetScale);
      this.root.spriteCache.set(key, sprite);
      built = true;
    }

    // Draw the sprite to our context. `drawTo` returns void; even if it
    // didn't, we'd discard it to preserve the one-level-only return.
    this.apply(xform);
    sprite.drawTo(this.ctx, targetScale);
    this.restore();
    if (built) {
      this.stats.instances.spritesBuilt += 1;
    } else {
      this.stats.instances.spritesCached += 1;
    }
    return "drawn";
  }
}
